/**
 * **The interrupt window** (`npc-gaps` 2.1): a watcher's reaction that interrupts a move before it
 * applies and makes the MOVER choose. Impending Threat (Archer): "A hostile character in line of
 * sight and Range 10 from you moves … The character must choose: lose the triggering
 * action/movement, or continue but let you immediately attack them with a single weapon." Moving
 * Target (Sniper): "A character in line of sight and range 20 moves. Interrupt the movement. The
 * character must either give up the triggering action/movement, or be immediately attacked by you
 * using the Anti-materiel Rifle. If the rifle isn't loaded, you instead reload it."
 *
 * **The clause** is a `bystander` move clause matched on `phase: "declared"`, `optional`: `self`
 * is the watcher and `target` the mover, so hostility, line of sight and range are ordinary
 * `condition` predicates, tested with the mover at its start. Its `effect` is empty: what the
 * reaction does is this module's. The shot it grants is the carrying entity's row in
 * `REACTION_SHOT_GRANTS` (`economy/reactionShot.ts`), keyed on the entity id: the status that row
 * names is what this module plants on the mover, and the weapon rule is the row's.
 *
 * **Three questions, each a halt of the mover's `request_move`, answered by re-submitting it:**
 *   1. the watcher: Use or Decline ({@link INTERRUPT_CONSUMER}, an ordinary move-checkpoint offer,
 *      so it sorts with Overwatch and a decline holds for the turn);
 *   2. the mover: give up the move, or continue ({@link interruptChoicePromptId});
 *   3. on continue, the watcher's shot ({@link interruptShotPromptId}): the move holds, the grant
 *      status commits on the mover, and "take" arms the watcher's reaction attack exactly as an
 *      Overwatch take does (the browser's take flow keys on any checkpoint prompt's `take`). The
 *      shot spends the reaction. With the grant's weapon unloaded (Moving Target's rifle), the
 *      watcher reloads instead, spending the reaction, and the move goes on.
 * Giving up rejects the move and spends the watcher's reaction.
 *
 * A grant with `moverChooses: false` (Suppress's Moving Target: "interrupt with a single weapon's
 * attack") skips question 2. Its `ends` statuses come off the mover once the shot is taken.
 *
 * Not built: Impending Threat's "or takes action" half (the declared-activation window).
 */
import {
  DISENGAGED_STATUS,
  feintedBy,
  hasStatus,
  isCharacter,
  isWeaponLoaded,
  loadedResourceKey,
  weaponInstanceKey,
  worldFor,
  type Combatant,
  type EncounterCommand,
  type EncounterState,
  type ResolvedStep,
} from "@lancer-vtt/domain";
import { NeedsDecisionSignal, SEED_OVERLAY, combatantEffectIds, evaluateMatch, evaluatePredicate, gatherPilotHandlers, gatherStatusHandlers, gatherSystemHandlers, type Handler, type LoadedOverlay, type MoveEvent } from "./effects";
import type { MoveCheckpointConsumer, MoveCheckpointContext, MoveCheckpointOffer } from "./checkpointRegistry";
import { REACTION_SHOT_GRANTS, type ReactionShotGrantSpec } from "./economy/reactionShot";
import { hasLiveMoveReactionDecline } from "./moveReactionDecline";
import { reactionAvailable } from "./reactionRegistry";
import { reactionSpendCommands } from "./events/gameEvents";

/** The consumer id: the watcher's Use/Decline offer. */
export const INTERRUPT_ID = "interrupt";
/** The mover's choice and the watcher's shot, two prompts after the offer. */
const INTERRUPT_CHOICE_ID = "interrupt_choice";
const INTERRUPT_SHOT_ID = "interrupt_shot";
export const INTERRUPT_USE_OPTION = "use";
export const INTERRUPT_DECLINE_OPTION = "decline";
export const INTERRUPT_GIVE_UP_OPTION = "give_up";
export const INTERRUPT_CONTINUE_OPTION = "continue";
/** The shot prompt's options: "take" is the checkpoint take every client already routes into its reaction attack. */
export const INTERRUPT_TAKE_OPTION = "take";

/**
 * `checkpointRegistry.ts`'s `moveCheckpointPromptId`, restated: that module lists this one's
 * consumer, so importing its value here would make a load-order cycle. `moveInterrupts.test.ts`
 * pins the two equal.
 */
export function moveCheckpointPromptId(consumerId: string, watcherId: string): string {
  return `move_checkpoint:${consumerId}:${watcherId}`;
}

/**
 * The watcher's Use/Decline offer for the interrupt `entityId` carries. Keyed on the entity as well
 * as the watcher: one watcher can carry two (an Archer's Impending Threat and Suppress's Moving
 * Target), and the decline a re-submit records names the same `interrupt#<entity>` consumer.
 */
export function interruptOfferPromptId(entityId: string, watcherId: string): string {
  return moveCheckpointPromptId(`${INTERRUPT_ID}#${entityId}`, watcherId);
}

/** The mover's give-up-or-continue prompt for the watcher's interrupt. Its decider is the mover. */
export function interruptChoicePromptId(entityId: string, watcherId: string): string {
  return moveCheckpointPromptId(`${INTERRUPT_CHOICE_ID}#${entityId}`, watcherId);
}

/** The watcher's shot prompt after the mover continued. */
export function interruptShotPromptId(entityId: string, watcherId: string): string {
  return moveCheckpointPromptId(`${INTERRUPT_SHOT_ID}#${entityId}`, watcherId);
}

/** One watcher's interrupt: the clause, its title, and the shot it grants. */
type Interrupt = { watcher: Combatant; h: Handler; title: string; grant: ReactionShotGrantSpec };

/** Is this an interrupt clause? */
function isInterruptClause(effect: Handler["effect"]): boolean {
  return effect.on === "move" && effect.optional === true && effect.bystander === true && effect.match?.family === "move" && effect.match.phase === "declared";
}

/** Every interrupt clause a watcher carries that has a grant row, in carrier order. */
function interruptsOf(state: EncounterState, watcher: Combatant, overlay: LoadedOverlay): { h: Handler; grant: ReactionShotGrantSpec }[] {
  const handlers = [
    ...gatherStatusHandlers(state, watcher.id, overlay),
    ...gatherPilotHandlers({ carrierId: watcher.id, pilotEffectIds: combatantEffectIds(watcher), overlay, family: "move" }),
    ...gatherSystemHandlers({ state, carrierId: watcher.id, overlay, family: "move" }),
  ];
  return handlers.flatMap((h) => {
    if (!isInterruptClause(h.effect)) return [];
    const grant = REACTION_SHOT_GRANTS.find((g) => g.reactionId === h.entityId);
    return grant !== undefined ? [{ h, grant }] : [];
  });
}

/** The declared move's event, `phase: "declared"`. */
function declaredEvent(state: EncounterState, move: MoveCheckpointContext["declaredMove"]): MoveEvent {
  const distance = move.spaces ?? worldFor(state).distance(move.from, move.to);
  return { family: "move", phase: "declared", distance, from: move.from, to: move.to, voluntary: move.voluntary ?? true, ...(move.path !== undefined ? { path: move.path } : {}) };
}

/** Every watcher whose interrupt is eligible against this declared move, in id order. */
function eligibleInterrupts(ctx: MoveCheckpointContext): Interrupt[] {
  const { state, declaredMove } = ctx;
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  const mover = state.combatants[declaredMove.moverId];
  if (!mover || !isCharacter(mover) || hasStatus(mover.statuses, DISENGAGED_STATUS)) return [];
  // The condition is tested with the mover at its start: the trigger is the move beginning.
  const atStart: EncounterState = { ...state, combatants: { ...state.combatants, [mover.id]: { ...mover, pos: declaredMove.from } } };
  const event = declaredEvent(state, declaredMove);
  const out: Interrupt[] = [];
  for (const watcherId of Object.keys(state.combatants).sort()) {
    if (watcherId === mover.id) continue;
    const watcher = state.combatants[watcherId]!;
    if (!watcher.pos || hasStatus(watcher.statuses, "Destroyed") || feintedBy(mover, watcherId)) continue;
    for (const { h, grant } of interruptsOf(state, watcher, overlay)) {
      if (h.effect.match && !evaluateMatch(h.effect.match, event)) continue;
      if (h.effect.condition && !evaluatePredicate(h.effect.condition, { state: atStart, actors: { self: watcherId, target: mover.id }, event })) continue;
      if (!reactionAvailable(state, watcherId, { name: grant.reactionId })) continue;
      if (hasLiveMoveReactionDecline(state, { watcherId, moverId: mover.id, consumerId: `${INTERRUPT_ID}#${grant.reactionId}` })) continue;
      out.push({ watcher, h, title: h.effect.name ?? grant.verb, grant });
    }
  }
  return out;
}

/** The watcher's Use/Decline offer: the first of the three questions. */
function compileInterruptOffers(ctx: MoveCheckpointContext): MoveCheckpointOffer[] {
  const moverName = ctx.state.combatants[ctx.declaredMove.moverId]?.name ?? ctx.declaredMove.moverId;
  return eligibleInterrupts(ctx).map(({ watcher, title, grant }) => ({
    id: `${INTERRUPT_ID}#${grant.reactionId}:${watcher.id}`,
    watcherId: watcher.id,
    who: () => watcher.id,
    promptId: () => interruptOfferPromptId(grant.reactionId, watcher.id),
    prompt: () => `${watcher.name} may interrupt ${moverName}'s move with ${title}: it must give up the move or be attacked — use it?`,
    title,
    options: [
      { id: INTERRUPT_USE_OPTION, label: "Use" },
      { id: INTERRUPT_DECLINE_OPTION, label: "Decline" },
    ],
  }));
}

/** The interrupt offer as a move-checkpoint consumer, beside Overwatch. */
export const INTERRUPT_CONSUMER: MoveCheckpointConsumer = { id: INTERRUPT_ID, compile: compileInterruptOffers };

/** What the interrupts a cleared gather leaves owe the move. */
export type InterruptSettlement =
  /** Every interrupt is answered: the move goes on, after these steps (the shot markers cleared, a reload). */
  | { kind: "go"; steps: ResolvedStep[]; spends: { combatantId: string; name: string }[] }
  /** The mover gave up: the move is rejected, the watcher's reaction spent. */
  | { kind: "give_up"; message: string; spends: { combatantId: string; name: string }[] }
  /** The mover continued and the watcher has not shot: plant the marker (these steps), then ask. */
  | { kind: "shoot"; steps: ResolvedStep[]; ask: NeedsDecisionSignal };

/**
 * Settle the interrupts the re-submitted move's answers accepted (the offer answered Use), in
 * watcher-id order: ask the mover, then the watcher's shot. Throws the mover's choice when it is
 * unanswered; returns `shoot` when the shot must be asked after its marker commits. Read off the
 * recorded answers, not a recompile, because the shot spends the reaction the offer gated on.
 */
export function settleInterrupts(args: { state: EncounterState; moverId: string; choices: Record<string, string> | undefined; overlay: LoadedOverlay }): InterruptSettlement {
  const { state, moverId, choices, overlay } = args;
  const mover = state.combatants[moverId];
  const steps: ResolvedStep[] = [];
  const spends: { combatantId: string; name: string }[] = [];
  if (!mover || choices === undefined) return { kind: "go", steps, spends };
  // Every accepted offer, read back off its key: `move_checkpoint:interrupt#<entity>:<watcher>`.
  const prefix = `move_checkpoint:${INTERRUPT_ID}#`;
  const used = Object.keys(choices)
    .filter((k) => choices[k] === INTERRUPT_USE_OPTION && k.startsWith(prefix))
    .map((k) => {
      const rest = k.slice(prefix.length);
      const colon = rest.lastIndexOf(":");
      return { entityId: rest.slice(0, colon), watcherId: rest.slice(colon + 1) };
    })
    .sort((a, b) => (a.watcherId === b.watcherId ? a.entityId.localeCompare(b.entityId) : a.watcherId.localeCompare(b.watcherId)));
  for (const { entityId, watcherId } of used) {
    const watcher = state.combatants[watcherId];
    const found = watcher !== undefined ? interruptsOf(state, watcher, overlay).find((i) => i.grant.reactionId === entityId) : undefined;
    if (watcher === undefined || found === undefined) continue;
    const { h, grant } = found;
    const title = h.effect.name ?? grant.verb;
    const marked = mover.statuses.some((s) => s.name === grant.status && s.source === watcherId);
    const choice = grant.moverChooses === false ? INTERRUPT_CONTINUE_OPTION : choices[interruptChoicePromptId(entityId, watcherId)];
    if (choice === undefined) {
      throw new NeedsDecisionSignal(interruptChoicePromptId(entityId, watcherId), `${watcher.name}'s ${title}: give up the move, or continue and be attacked?`, {
        options: [
          { id: INTERRUPT_GIVE_UP_OPTION, label: "Give up the move" },
          { id: INTERRUPT_CONTINUE_OPTION, label: "Continue, be attacked" },
        ],
        who: moverId,
        title,
      });
    }
    if (choice === INTERRUPT_GIVE_UP_OPTION) {
      return { kind: "give_up", message: `${mover.name} gives up its move (${watcher.name}'s ${title})`, spends: [{ combatantId: watcherId, name: grant.reactionId }] };
    }
    const unloaded = (watcher.base.weapons ?? []).find((w) => grant.weapon(w) && w.loading === true && !isWeaponLoaded(watcher.current.resources, weaponInstanceKey(w)));
    if (unloaded !== undefined) {
      // "If the rifle isn't loaded, you instead reload it."
      steps.push({ op: "resource_delta", target: watcherId, key: loadedResourceKey(weaponInstanceKey(unloaded)), delta: 1 });
      spends.push({ combatantId: watcherId, name: grant.reactionId });
      continue;
    }
    const shot = choices[interruptShotPromptId(entityId, watcherId)];
    if (shot === undefined) {
      const ask = new NeedsDecisionSignal(interruptShotPromptId(entityId, watcherId), `${mover.name} keeps moving: ${watcher.name} attacks with ${title} before it moves.`, {
        options: [
          { id: INTERRUPT_TAKE_OPTION, label: "Take the shot" },
          { id: INTERRUPT_DECLINE_OPTION, label: "Hold fire" },
        ],
        who: watcherId,
        title,
      });
      const plant: ResolvedStep[] = marked ? [] : [{ op: "status_add", target: moverId, status: grant.status, expiry: { kind: "this_turn" }, appliedTurn: state.activation.turnOrdinal, source: watcherId }];
      return { kind: "shoot", steps: plant, ask };
    }
    if (marked) steps.push({ op: "status_remove", target: moverId, status: grant.status });
    // A taken shot ends what the reaction's own effect planted (Suppress's mark and Impaired), only the instances this watcher sourced.
    if (shot === INTERRUPT_TAKE_OPTION) {
      for (const name of grant.ends ?? []) if (mover.statuses.some((st) => st.name === name && st.source === watcherId)) steps.push({ op: "status_remove", target: moverId, status: name });
    }
  }
  return { kind: "go", steps, spends };
}

/** The settlement's commands: an `apply_resolved` for its steps, then its reaction spends. */
export function settlementCommands(actorId: string, s: { steps?: ResolvedStep[]; spends?: { combatantId: string; name: string }[] }): EncounterCommand[] {
  const out: EncounterCommand[] = [];
  if (s.steps !== undefined && s.steps.length > 0) out.push({ type: "apply_resolved", actorId, steps: s.steps, kind: "move_interrupt" });
  out.push(...reactionSpendCommands(actorId, s.spends));
  return out;
}

